import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import axios from "axios";
import * as signalR from "@microsoft/signalr";
import { useLocation } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import { auth0Config, bypassAuth } from "../Auth/auth0Config";

/** One presence row per online admin (deduped by identity server-side): who they are + which
 *  page(s) their open tabs are viewing. One admin with several tabs = one row, pages merged.
 *  `userId` links to /account/{userId}; `avatarKey`/`email` render the avatar. */
export type PresenceUser = { user: string; pages: string[]; userId?: string | null; email?: string | null; avatarKey?: string | null; pictureUrl?: string | null };

export type RealtimeState = {
	/** True while the SignalR connection to /hubs/admin is open. */
	connected: boolean;
	/** Number of distinct admins online : NOT connections. The hub dedupes a user's tabs, so
	 *  this always equals `users.length` (three tabs of one admin count as one). */
	online: number;
	users: PresenceUser[];
};

/** Handler for a hub event payload (activity / deploy / lock). */
type RealtimeHandler = (payload: any) => void;

export type RealtimeContextValue = RealtimeState & {
	/** Subscribe to a server event ("activity" | "deploy" | "lock" | "preview-sync"); returns an unsubscribe fn. */
	subscribe: (event: string, handler: RealtimeHandler) => () => void;
	/** Tell the hub which page this admin is on (drives presence). No-op until connected. */
	heartbeat: (page: string) => void;
};

const noopUnsub = () => {};

const RealtimeContext = createContext<RealtimeContextValue>({
	connected: false,
	online: 0,
	users: [],
	subscribe: () => noopUnsub,
	heartbeat: () => {}
});

/** Subscribe to the live admin realtime state (connection + presence). */
export const useRealtime = (): RealtimeContextValue => useContext(RealtimeContext);

/**
 * Subscribe a component to a streamed hub event for its lifetime. The latest `handler` is always
 * called (kept in a ref) so callers can pass an inline closure without re-subscribing every render.
 */
export const useRealtimeEvent = (event: string, handler: RealtimeHandler): void => {
	const { subscribe } = useContext(RealtimeContext);
	const handlerRef = useRef(handler);
	handlerRef.current = handler;
	useEffect(() => subscribe(event, (payload) => handlerRef.current(payload)), [event, subscribe]);
};

/** Server events that the provider relays to component subscribers (presence is handled separately). */
const RELAYED_EVENTS = ["activity", "deploy", "lock", "preview-sync", "content-saved", "ticket", "notification", "permissions-changed"] as const;

/**
 * Opens a single SignalR connection to the admin hub for the lifetime of the authenticated app and
 * exposes the live state + an event bus via context. Auto-reconnects; degrades silently to "not
 * connected" if the hub/Redis is unavailable (the rest of the admin keeps working on REST). Under
 * Auth0 it supplies the access token via accessTokenFactory (the server reads it from ?access_token=
 * for the WebSocket). Presence is driven by a Heartbeat call on every route change.
 */
export const RealtimeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
	const [state, setState] = useState<RealtimeState>({ connected: false, online: 0, users: [] });
	const { getAccessTokenSilently } = useAuth0();
	const connectionRef = useRef<signalR.HubConnection | null>(null);
	// event -> set of subscriber handlers. A plain ref so subscribe() is stable and never re-runs the
	// connection effect; the connection dispatches into these sets as events arrive.
	const subscribersRef = useRef<Map<string, Set<RealtimeHandler>>>(new Map());
	// Last page we told the hub about, replayed once the connection (re)opens.
	const lastPageRef = useRef<string>("");
	const location = useLocation();

	const subscribe = useCallback((event: string, handler: RealtimeHandler) => {
		let set = subscribersRef.current.get(event);
		if (!set) {
			set = new Set();
			subscribersRef.current.set(event, set);
		}
		set.add(handler);
		return () => {
			subscribersRef.current.get(event)?.delete(handler);
		};
	}, []);

	const heartbeat = useCallback((page: string) => {
		lastPageRef.current = page;
		const connection = connectionRef.current;
		if (connection && connection.state === signalR.HubConnectionState.Connected) {
			connection.invoke("Heartbeat", page).catch(() => {});
		}
	}, []);

	useEffect(() => {
		const base = (axios.defaults.baseURL || "").replace(/\/+$/, "");
		const hubUrl = `${base}/hubs/admin`;

		const connection = new signalR.HubConnectionBuilder()
			.withUrl(hubUrl, {
				// In bypass-auth dev the server authenticates every connection; under Auth0 we hand
				// SignalR a token getter (it appends ?access_token=… on the WebSocket negotiate).
				accessTokenFactory: bypassAuth
					? undefined
					: async () => {
							try {
								return await getAccessTokenSilently(
									auth0Config.audience
										? { authorizationParams: { audience: auth0Config.audience } }
										: undefined
								);
							} catch {
								return "";
							}
						}
			})
			.withAutomaticReconnect()
			.configureLogging(signalR.LogLevel.Warning)
			.build();
		connectionRef.current = connection;

		connection.on("presence", (payload: { online?: number; users?: PresenceUser[] }) => {
			setState((s) => ({ ...s, online: payload?.online ?? 0, users: payload?.users ?? [] }));
		});

		// Fan each relayed event out to every component subscriber.
		for (const event of RELAYED_EVENTS) {
			connection.on(event, (payload: unknown) => {
				const handlers = subscribersRef.current.get(event);
				if (!handlers) return;
				handlers.forEach((handler) => {
					try {
						handler(payload);
					} catch {
						/* a bad subscriber must not break the stream */
					}
				});
			});
		}

		const replayHeartbeat = () => {
			if (lastPageRef.current) {
				connection.invoke("Heartbeat", lastPageRef.current).catch(() => {});
			}
		};

		connection.onreconnected(() => {
			setState((s) => ({ ...s, connected: true }));
			replayHeartbeat();
		});
		connection.onreconnecting(() => setState((s) => ({ ...s, connected: false })));
		connection.onclose(() => setState((s) => ({ ...s, connected: false })));

		let cancelled = false;
		connection
			.start()
			.then(() => {
				if (cancelled) return;
				setState((s) => ({ ...s, connected: true }));
				replayHeartbeat();
			})
			.catch(() => {
				if (!cancelled) setState((s) => ({ ...s, connected: false }));
			});

		return () => {
			cancelled = true;
			connection.stop().catch(() => {});
		};
		// One connection for the session; getAccessTokenSilently is stable, so empty deps is intended.
	}, []);

	// Report the current page to the hub on every navigation so presence shows who is where.
	useEffect(() => {
		heartbeat(location.pathname);
	}, [location.pathname, heartbeat]);

	// Keep presence fresh even when the admin is not navigating: without a periodic heartbeat an
	// idle-but-connected session's server-side entry ages past the staleness window and gets pruned,
	// making the online count flicker down (e.g. showing 1 while 2 are actually connected).
	useEffect(() => {
		const id = setInterval(() => heartbeat(lastPageRef.current || "/"), 30000);
		return () => clearInterval(id);
	}, [heartbeat]);

	const value: RealtimeContextValue = { ...state, subscribe, heartbeat };
	return <RealtimeContext.Provider value={value}>{children}</RealtimeContext.Provider>;
};
